import React, { useContext, useEffect, useState, useCallback, useRef } from 'react';
import {
  Image,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  Animated as ReactAnimated,
  FlatList,
  Linking,
  Dimensions,
  ActivityIndicator,
} from 'react-native';
import Animated, {
  Easing,
  measure,
  runOnUI,
  useAnimatedRef,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { isTablet } from 'react-native-device-info';
import { EventRegister } from 'react-native-event-listeners';
import AppContext from '../../navigation/Context';
import colors from '../../resources/colors';
import images from '../../resources/images';
import { useToast } from 'react-native-toast-notifications';
import { format, isBefore } from 'date-fns';
import { useNavigation } from '@react-navigation/core';
import DataController from '../../controllers/DataController';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { createStackNavigator } from '@react-navigation/stack';
import AuthenticationController from '../../controllers/AuthenticationController';
import DatabaseService from '../../controllers/DatabaseController';
import AsyncStorage from '@react-native-async-storage/async-storage';

const iOSURL = 'https://apps.apple.com/gb/app/lectionary/id583885035';
const androidURL = 'https://play.google.com/store/apps/details?id=com.aimer.lectionary';
const forgerPasswordURL = 'https://myaccount.hymnsam.co.uk/forgot-password/';
const contactURL = 'https://aimermedia.com/contact-aimer/';
const subscriptionURL = 'https://www.chpublishing.co.uk/apps/lectionary';
const sharingLink = 'https://www.chpublishing.co.uk/app/sharing';

const Header = () => {
  const iOSPressed = () => DataController.openLink(iOSURL);

  const androidPressed = () => DataController.openLink(androidURL);

  const sharingPressed = () => DataController.openLink(sharingLink);
  return (
    <View style={styles.cardContainer}>
      <View>
        <View style={styles.lectionaryContainer}>
          <Image
            style={styles.lectionaryIcon}
            source={images.Icon_Lectionary_Active}
            resizeMode="stretch"
          />
          <Text style={styles.lectionaryText} allowFontScaling={false}>
            {'Lectionary'}
          </Text>
        </View>
        <Text style={styles.headerRed}>
          {'The official Common Worship Lectionary app from the Church of England'}
        </Text>
        <Text style={styles.normalText}>
          {'Follow the Church of England’s pattern of daily readings with the Lectionary app. View Collects and Post-Communions, generate customized service sheets, and read biographies of individuals commemorated in the Common Worship calendar.'}
        </Text>
        <Text style={[styles.normalText, { marginTop: 8, }]}>
          {'To use the web app, sign in below using your Hymns Ancient & Modern account.'}
        </Text>
        <Text style={[styles.normalText, { marginTop: 8, }]}>
          {'The app is also available for '}
          <TouchableOpacity onPress={iOSPressed}>
            <Text style={styles.linkText}>
              {'iPhone and iPad'}
            </Text>
          </TouchableOpacity>
          {' and '}
          <TouchableOpacity onPress={androidPressed}>
            <Text style={styles.linkText}>
              {'Android phones and tablets'}
            </Text>
          </TouchableOpacity>
          {'.'}
        </Text>
        <Text style={[styles.normalText, { marginTop: 8, }]}>
          {'If you purchased your Lectionary subscription from the Apple App store or Google Play store, please follow the instructions on this link before logging into this website for the first time: '}
          <TouchableOpacity>
            <Text style={styles.linkText} onPress={sharingPressed}>
              {'https://www.chpublishing.co.uk/app/sharing'}
            </Text>
          </TouchableOpacity>
        </Text>
        {/* <Text style={[styles.normalText, { marginTop: 8, }]}>
          {'Sign in below using your Hymns Ancient & Modern account.'}
        </Text> */}
      </View>
    </View>
  )
};

const SignIn = () => {
  const emailRef = useRef(null);
  const passwordRef = useRef(null);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordHidden, setPasswordHidden] = useState(true);
  const [signingIn, setSigningIn] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [errors, setErrors] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    focusEmail();

    EventRegister.addEventListener('auth/signing-in', (value) => {
      if (signingIn !== value) {
        setSigningIn(value);
      }
    });

    EventRegister.addEventListener('auth/sign-in-failed', (value) => {
      __DEV__ && console.log(value);

      setErrors(value.state);
      setErrorMessage(value.error?.message === 'Failed to fetch' ? 'Server error' : value.error);
      setSigningIn(false);
    });

    return () => {
      EventRegister.removeEventListener('auth/signing-in');
      EventRegister.removeEventListener('auth/sign-in-failed');
    }
  }, []);

  const signIn = async () => {
    try {
      let user = await AuthenticationController.login(email, password);

      if (user) {
        let subscriptions = await AuthenticationController.getSubscriptions(user.email, user.token, user.requestToken);
        __DEV__ && console.log(subscriptions);
        if (subscriptions && subscriptions.length > 0) {
          subscriptions = subscriptions.filter((item) => item.Status === 1);
          subscriptions = subscriptions.sort((a, b) => new Date(b.ExpiryDate) - new Date(a.ExpiryDate));

          if (subscriptions.length > 0) {
            const latestSubscription = subscriptions[0];
            const isValid = isBefore(new Date(), new Date(latestSubscription.ExpiryDate));

            __DEV__ && console.log(isValid, user);

            if (isValid) {
              await AuthenticationController.saveUser(user);
              await DataController.saveHymnsSubscription(subscriptions);

              const bookmarks = await DatabaseService.getAllBookmarks(user.email, user.requestToken);
              const notes = await DatabaseService.getAllNotes(user.email, user.requestToken);

              __DEV__ && console.log('bookmarks', bookmarks, notes);

              if (bookmarks) {
                await AsyncStorage.setItem('bookmarks', JSON.stringify(bookmarks));
                EventRegister.emit('bookmarks/updated');
              }

              if (notes) {
                await AsyncStorage.setItem('notes', JSON.stringify(notes));
                EventRegister.emit('notes/updated');
              }

              EventRegister.emit('auth/signing-in', false);
              EventRegister.emit('auth/user-changed', user);
            } else {
              EventRegister.emit('auth/sign-in-failed', { state: true, error: 'You do not have a valid subscription for the Lectionary app' });
            }
          } else {
            EventRegister.emit('auth/sign-in-failed', { state: true, error: 'You do not have a valid subscription for the Lectionary app' });
          }
        } else {
          EventRegister.emit('auth/sign-in-failed', { state: true, error: 'You do not have a subscription for the Lectionary app' });
        }
      } else {
        EventRegister.emit('auth/sign-in-failed', { state: true, error: 'Incorrect details' });
      }
    } catch (error) {
      console.log(error);
      EventRegister.emit('auth/sign-in-failed', { state: true, error });
    }
  };

  const focusEmail = () => {
    try {
      emailRef.current?.focus();
    } catch (error) {
      console.log(error);
    }
  };

  const emailChanged = (input) => {
    setEmail(input.trim());
    setErrors(false);
    setErrorMessage('');
  };

  const passwordChanged = (input) => {
    setPassword(input);
    setErrors(false);
    setErrorMessage('');
  };

  const inputFocused = () => {
    setErrors(false);
    setErrorMessage('');
  };

  const onEmailFocused = () => {
    setEmailFocused(true);
    setErrors(false);
    setErrorMessage('');
  };

  const emailBlurred = () => {
    setEmailFocused(false);
    setErrors(false);
    setErrorMessage('');
  };

  const onPasswordFocused = () => {
    setPasswordFocused(true);
    setErrors(false);
    setErrorMessage('');
  };

  const passwordBlurred = () => {
    setPasswordFocused(false);
    setErrors(false);
    setErrorMessage('');
  };

  const forgotPasswordPressed = () => DataController.openLink(forgerPasswordURL);

  const contactSupportPressed = () => DataController.openLink(contactURL);

  const togglePasswordVisibility = () => setPasswordHidden(!passwordHidden);

  const inputsEmpty = email === '' && password === '';

  let showSubscriptionLink = false;

  if (Platform.OS === 'web' && errorMessage?.includes('subscription')) {
    showSubscriptionLink = true;
  }

  return (
    <View style={[styles.cardContainer, { marginTop: 15, marginBottom: 66 }]}>
      <View
        style={[styles.loginHeader, {
          flexDirection: Dimensions.get('window').width > 600 ? 'row' : 'column',
          alignItems: Dimensions.get('window').width > 600 ? 'center' : 'flex-start',
        }]}>
        <Text style={styles.loginHeaderText} allowFontScaling={false}>
          {'Sign in to hymnsam.co.uk'}
        </Text>
        <View style={{ flexDirection: Dimensions.get('window').width > 600 ? 'row' : 'column' }}>
          <TouchableOpacity
            style={{
              marginEnd: 18,
              marginTop: Dimensions.get('window').width > 600 ? 0 : 4,
              marginBottom: Dimensions.get('window').width > 600 ? 0 : 4,
            }}
            onPress={forgotPasswordPressed}>
            <Text style={styles.signInLinkText} allowFontScaling={false}>
              {'Forgot password?'}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={contactSupportPressed}>
            <Text style={styles.signInLinkText} allowFontScaling={false}>
              {'Contact support'}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
      <View
        style={{
          marginTop: 12,
          flexDirection: Dimensions.get('window').width > 600 ? 'row' : 'column'
        }}
        accessibilityRole="form">
        <View style={{ flex: 1 }}>
          <Text style={styles.textInputHeader} allowFontScaling={false}>
            {'Email'}
          </Text>
          <TextInput
            ref={emailRef}
            style={styles.textInput}
            editable={!signingIn}
            numberOfLines={1}
            placeholder={'joe.bloggs@example.co.uk'}
            placeholderTextColor={colors.grey70}
            value={email}
            onChangeText={(input) => emailChanged(input)}
            onFocus={onEmailFocused}
            onBlur={emailBlurred}
            autoCapitalize="none"
            autoComplete="email"
            keyboardType="email-address"
            returnKeyType="next"
            onSubmitEditing={() => passwordRef.current?.focus()}
          />
        </View>
        <View style={{
          height: Dimensions.get('window').width > 600 ? 0 : 16,
          width: Dimensions.get('window').width > 600 ? 16 : 0,
        }}
        />
        <View style={{ flex: 1 }}>
          <Text style={styles.textInputHeader} allowFontScaling={false}>
            {'Password'}
          </Text>
          <View>
            <TextInput
              ref={passwordRef}
              style={[styles.textInput, { paddingEnd: 50 }]}
              editable={!signingIn}
              placeholder={'Password'}
              placeholderTextColor={colors.grey70}
              numberOfLines={1}
              secureTextEntry={passwordHidden}
              clearTextOnFocus={false}
              value={password}
              onChangeText={(input) => passwordChanged(input)}
              onFocus={onPasswordFocused}
              onBlur={passwordBlurred}
              autoCapitalize="none"
              autoComplete="password"
              onSubmitEditing={() => {
                if (!signingIn && !inputsEmpty) {
                  signIn();
                }
              }}
            />
            <TouchableOpacity
              style={{
                position: 'absolute',
                bottom: 0,
                right: 0,
              }}
              onPress={togglePasswordVisibility}>
              <Image
                style={styles.buttonMenu}
                source={passwordHidden ? images.Button_View_Hide : images.Button_View_Show}
              />
            </TouchableOpacity>
          </View>
        </View>
        <TouchableOpacity
          style={[styles.signInButton, {
            opacity: inputsEmpty ? 0.4 : 1,
          }]}
          disabled={signingIn || inputsEmpty}
          onPress={signIn}>
          {signingIn ? (
            <ActivityIndicator size="small" color={colors.accentWhite} style={styles.activityIndicator} />
          ) :
            (
              <Text style={styles.signInButtonText}>
                {'Sign in'}
              </Text>
            )}
        </TouchableOpacity>
      </View>
      {errors && (
        <View style={{ marginTop: 12 }}>
          <Text style={styles.errorMessage}>{errorMessage}</Text>
          {showSubscriptionLink && (
            <Text style={styles.errorMessage}>
              <TouchableOpacity onPress={() => DataController.openLink(subscriptionURL)}>
                <Text style={[styles.errorMessage, { textDecorationLine: 'underline' }]}>
                  {'Purchase or renew your subscription'}
                </Text>
              </TouchableOpacity>
            </Text>
          )}
        </View>
      )}
    </View>
  );
};

const Footer = () => {
  const iOSPressed = () => DataController.openLink(iOSURL);

  const androidPressed = () => DataController.openLink(androidURL);

  return (
    <View>
      <Image
        style={{
          width: '100%',
          aspectRatio: 570 / 344,
        }}
        source={images.Image_Landing_Devices}
      />
      <Text style={styles.footerText}>
        {'Get the app on your phone or tablet'}
      </Text>
      <View
        style={{
          marginTop: 16,
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: Dimensions.get('window').width > 300 ? 'row' : 'column',
        }}>
        <TouchableOpacity onPress={iOSPressed}>
          <Image
            style={{
              width: 132,
              height: 44,
            }}
            source={images.Label_App_Store}
          />
        </TouchableOpacity>
        <View
          style={{
            marginEnd: Dimensions.get('window').width > 300 ? 12 : 0,
            marginBottom: Dimensions.get('window').width > 300 ? 0 : 12,
          }}
        />
        <TouchableOpacity onPress={androidPressed}>
          <Image
            style={{
              width: 150,
              height: 44,
            }}
            source={images.Label_Google_Play}
          />
        </TouchableOpacity>
      </View>
    </View>
  )
};

const LandingScreen = ({ navigation }) => {
  const toast = useToast();
  const context = useContext(AppContext);

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.scrollViewContainer}>
      <Header />
      <SignIn />
      <Footer />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: colors.accentWhite,
    flex: 1,
    height: '100%',
  },
  backButton: {
    flexDirection: 'row',
    marginStart: 4,
    alignItems: 'center',
  },
  backButtonText: {
    fontFamily: 'FiraSans-Regular',
    fontSize: 17,
    fontWeight: 'normal',
    color: colors.primary,
    marginStart: -5,
  },
  scrollViewContainer: {
    paddingHorizontal: isTablet() || Platform.OS === 'web' ? 30 : 15,
    paddingTop: 32,
    paddingBottom: 40,
    width: '100%',
    maxWidth: 686,
    alignSelf: 'center',
  },
  headerRow: {
    flexDirection: 'row',
  },
  buttonMenu: {
    width: 44,
    height: 44,
    marginStart: 4,
  },
  menuIcon: {
    width: 44,
    height: 44,
    tintColor: colors.primary,
  },
  headerTitle: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 17,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    lineHeight: 24,
    letterSpacing: -0.1,
    textAlign: 'center',
    color: colors.primary,
  },
  cardContainer: {
    paddingVertical: 24,
    paddingHorizontal: 16,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: colors.grey40,
    backgroundColor: colors.accentWhite,
    overflow: 'hidden',
  },
  lectionaryContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  lectionaryIcon: {
    height: 49,
    width: 49,
    marginStart: -8,
    marginEnd: 6,
  },
  lectionaryText: {
    fontFamily: 'FiraSans-Bold',
    fontSize: 34,
    fontWeight: Platform.OS === 'ios' ? 'bold' : 'normal',
    fontStyle: 'normal',
    lineHeight: 44,
    letterSpacing: 0.4,
    color: colors.primary,
  },
  headerRed: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 17,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    fontStyle: 'normal',
    lineHeight: 24,
    letterSpacing: 0.1,
    color: colors.primary,
    marginTop: 8,
    marginBottom: 24,
  },
  normalText: {
    fontFamily: 'FiraSans-Regular',
    fontSize: 17,
    fontWeight: 'normal',
    fontStyle: 'normal',
    lineHeight: 24,
    color: colors.defaultText,
  },
  linkText: {
    fontFamily: 'FiraSans-Regular',
    fontSize: 17,
    fontWeight: 'normal',
    fontStyle: 'normal',
    lineHeight: 24,
    color: colors.blue,
    textDecorationLine: 'underline',
  },
  loginHeader: {
    marginTop: -8,
    justifyContent: 'space-between',
  },
  loginHeaderText: {
    fontFamily: 'FiraSans-Bold',
    fontSize: 20,
    fontWeight: Platform.OS === 'ios' ? 'bold' : 'normal',
    fontStyle: 'normal',
    lineHeight: 28,
    letterSpacing: 0.1,
    color: colors.primary,
  },
  signInLinkText: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 15,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    fontStyle: 'normal',
    lineHeight: 20,
    color: colors.blue,
    textDecorationLine: 'underline',
  },
  textInputHeader: {
    fontFamily: 'FiraSans-Regular',
    fontSize: 12,
    fontWeight: 'normal',
    fontStyle: 'normal',
    lineHeight: 16,
    color: colors.grey70,
    marginBottom: 4,
  },
  textInput: {
    height: 44,
    backgroundColor: colors.grey30,
    borderStyle: 'solid',
    borderWidth: 1,
    borderColor: colors.grey40,
    borderRadius: 4,
    paddingHorizontal: 16,
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 17,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    fontStyle: 'normal',
    letterSpacing: 0.1,
    color: colors.defaultText,
  },
  signInButton: {
    height: 44,
    width: 120,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.primary,
    borderRadius: 8,
    alignSelf: 'flex-end',
    marginTop: 16,
    marginStart: 16,
  },
  activityIndicator: {
    alignSelf: 'center',
  },
  signInButtonText: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 17,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    fontStyle: 'normal',
    letterSpacing: 0.1,
    textAlign: 'center',
    color: colors.accentWhite,
  },
  errorMessage: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 15,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    fontStyle: 'normal',
    lineHeight: 20,
    color: colors.primary,
  },
  footerText: {
    fontFamily: 'FiraSans-SemiBold',
    fontSize: 17,
    fontWeight: Platform.OS === 'ios' ? '600' : 'normal',
    fontStyle: 'normal',
    lineHeight: 24,
    letterSpacing: 0.1,
    textAlign: 'center',
    color: colors.defaultText,
    marginTop: 57,
  },
});

export default LandingScreen;
